import type { MisconceptionDefinition } from './types'

/** REK — Basisrekenen misconcepties. */
export const REK_MISCONCEPTIONS: MisconceptionDefinition[] = [
  {
    id: 'rek.carry-forgotten',
    label: 'Onthouden vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet de overdracht (carry) volledig bij cijferend optellen of vermenigvuldigen — schrijft géén onthoud-cijfer op of telt het in de volgende kolom niet mee. Onderscheid met rek.carry-wrong-value: dáár wordt wél een overdracht meegerekend, maar met het verkeerde cijfer.',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.borrow-forgotten',
    label: 'Lenen vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling weet dat aftrekken nodig is maar voert het leen-mechanisme niet (volledig) uit: vergeet het tiental boven te verlagen of het geleende tiental bij de eenheden op te tellen. Onderscheid met rek.subtract-reversed: dáár denkt de leerling dat aftrekken per kolom commutatief is en draait de bewerking om.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.order-of-operations',
    label: 'Voorrangsregels niet toegepast',
    module: 'Basisrekenen',
    description:
      'Leerling past de voorrangsregels (eerst haakjes, dan × en ÷ van links naar rechts, dan + en − van links naar rechts) niet of niet volledig toe. Vaak gecombineerd met rek.left-to-right (gewoon van links naar rechts uitrekenen) of rek.brackets-ignored.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.multiplication-table-error',
    label: 'Tafelfout',
    module: 'Basisrekenen',
    description:
      'Leerling kent een specifieke tafel niet uit het hoofd (bijv. 7×8=54).',
    typicalFamilies: [
      'REK-TAFELS-BASIS',
      'REK-TAFELS-UITBREIDEN',
      'REK-TAFELS-TOEPASSEN',
    ],
  },
  {
    id: 'rek.division-remainder-ignored',
    label: 'Rest genegeerd',
    module: 'Basisrekenen',
    description:
      'Leerling negeert de rest bij een deling of rondt verkeerd af.',
    typicalFamilies: ['REK-CIJF-DEEL'],
  },
  {
    id: 'rek.subtract-reversed',
    label: 'Aftrekken omgedraaid',
    module: 'Basisrekenen',
    description:
      'Leerling behandelt aftrekken per kolom alsof het commutatief is en trekt het kleinste cijfer van het grootste af, ongeacht boven/onder. Dit is een conceptuele fout (denkt dat 4−7 onmogelijk is dus rekent 7−4), niet enkel een uitvoeringsfout.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.negative-written',
    label: 'Negatief getal opgeschreven',
    module: 'Basisrekenen',
    description:
      'Leerling schrijft een negatief getal op in plaats van te lenen bij aftrekken.',
    typicalFamilies: ['REK-CIJF-MIN'],
  },
  {
    id: 'rek.column-skipped',
    label: 'Kolom overgeslagen',
    module: 'Basisrekenen',
    description: 'Leerling slaat een kolom over bij cijferend rekenen.',
    typicalFamilies: ['REK-CIJF-MIN', 'REK-CIJF-PLUS'],
  },
  {
    id: 'rek.column-misalignment',
    label: 'Kolommen verkeerd uitgelijnd',
    module: 'Basisrekenen',
    description:
      'Leerling schrijft de cijfers in de verkeerde kolom (eenheden onder tientallen, of komma niet uitgelijnd), waardoor de plaatswaarde verschuift en alle tussenresultaten fout worden.',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-MIN', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.carry-wrong-value',
    label: 'Verkeerd getal onthouden',
    module: 'Basisrekenen',
    description:
      'Leerling onthoudt het verkeerde getal bij de overdracht (bijv. eenheden i.p.v. tientallen).',
    typicalFamilies: ['REK-CIJF-PLUS', 'REK-CIJF-KEER'],
  },
  {
    id: 'rek.left-to-right',
    label: 'Links-naar-rechts zonder voorrang',
    module: 'Basisrekenen',
    description:
      'Leerling rekent van links naar rechts zonder de bewerkingsvolgorde toe te passen.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.brackets-ignored',
    label: 'Haakjes genegeerd',
    module: 'Basisrekenen',
    description:
      'Leerling negeert haakjes en past de standaard voorrangsregels toe.',
    typicalFamilies: ['REK-VOLGORDE'],
  },
  {
    id: 'rek.context-direction-wrong',
    label: 'Verkeerde bewerking bij verhaaltjessom',
    module: 'Basisrekenen',
    description:
      'Leerling kiest de verkeerde bewerking (bijv. optellen i.p.v. aftrekken) bij een contextvraag.',
    typicalFamilies: [
      'REK-CONTEXT-PLUS-MIN',
      'REK-CONTEXT-KEER-DEEL',
      'REK-CONTEXT-MEERSTAPS',
    ],
  },
  {
    id: 'rek.context-step-forgotten',
    label: 'Stap vergeten bij meerstapsprobleem',
    module: 'Basisrekenen',
    description:
      'Leerling berekent slechts een deel van een meerstapsprobleem en vergeet de laatste stap.',
    typicalFamilies: ['REK-CONTEXT-PLUS-MIN', 'REK-CONTEXT-MEERSTAPS'],
  },
  {
    id: 'rek.rounding-wrong-direction',
    label: 'Verkeerd afgerond bij schatten',
    module: 'Basisrekenen',
    description:
      'Leerling rondt af naar een niet-dichtstbijzijnd getal bij het schatten.',
    typicalFamilies: [
      'REK-SCHATTEN',
      'REK-CONTEXT-KEER-DEEL',
      'REK-CONTEXT-MEERSTAPS',
    ],
  },
  {
    id: 'rek.strategy-wrong-correction',
    label: 'Verkeerde correctie bij compenseren',
    module: 'Basisrekenen',
    description:
      'Leerling corrigeert de verkeerde kant op na compenseren (bijv. trekt af i.p.v. optellen).',
    typicalFamilies: ['REK-HANDIG-PLUS-MIN', 'REK-HANDIG-KEER'],
  },
  {
    id: 'rek.partial-calculation',
    label: 'Onvolledige berekening',
    module: 'Basisrekenen',
    description:
      'Leerling stopt halverwege de berekening en geeft een tussenresultaat als eindantwoord.',
    typicalFamilies: [
      'REK-CONTEXT-PLUS-MIN',
      'REK-CONTEXT-KEER-DEEL',
      'REK-CONTEXT-MEERSTAPS',
      'REK-TAFELS-TOEPASSEN',
    ],
  },
  {
    id: 'rek.operation-confused',
    label: 'Bewerkingen verward',
    module: 'Basisrekenen',
    description:
      'Leerling verwart bewerkingen (bijv. vermenigvuldigt i.p.v. optellen, of deelt i.p.v. aftrekken).',
    typicalFamilies: [
      'REK-CONTEXT-KEER-DEEL',
      'REK-GEMENGD-BASIS',
      'REK-TAFELS-TOEPASSEN',
    ],
  },
  {
    id: 'rek.divisor-dividend-swapped',
    label: 'Deler en deeltal omgedraaid',
    module: 'Basisrekenen',
    description:
      'Leerling draait deler en deeltal om (bv. leest 48 ÷ 6 als 6 ÷ 48, of berekent bij "verdeel 144 over 12 dozen" de deling 12 ÷ 144). Klassieke contextuele fout waarbij het kleinste getal automatisch als deler wordt aangenomen.',
    typicalFamilies: ['REK-CIJF-DEEL', 'REK-CONTEXT-KEER-DEEL'],
  },
  {
    id: 'rek.strategy-suboptimal',
    label: 'Kiest niet-handige strategie',
    module: 'Basisrekenen',
    description:
      'Leerling kiest een strategie die wel werkt maar niet handig is (bijv. cijferend i.p.v. factoren herkennen).',
    typicalFamilies: [
      'REK-HANDIG-KEER',
      'REK-HANDIG-DEEL',
      'REK-HANDIG-PLUS-MIN',
    ],
  },
  {
    id: 'rek.calculation-error',
    label: 'Rekenfout',
    module: 'Basisrekenen',
    description:
      'Leerling maakt een rekenfout bij vermenigvuldigen, delen of een tussenstap (niet gerelateerd aan een specifieke misconceptie).',
    typicalFamilies: [
      'REK-CONTEXT-KEER-DEEL',
      'REK-HANDIG-KEER',
      'REK-HANDIG-DEEL',
      'REK-GEMENGD-TOETS',
    ],
  },
  {
    id: 'rek.context-wrong-data',
    label: 'Verkeerde gegevens uit context gehaald',
    module: 'Basisrekenen',
    description:
      'Leerling gebruikt een verkeerd getal uit de opgave: pakt het verkeerde tarief, het verkeerde aantal of de verkeerde deler. De bewerking klopt vaak wel, maar de invoer is fout (bv. deelt door 20 i.p.v. 18, of gebruikt 4 i.p.v. 5 rondjes).',
    typicalFamilies: [
      'REK-CONTEXT-KEER-DEEL',
      'REK-CONTEXT-MEERSTAPS',
      'REK-HANDIG-KEER',
      'REK-HANDIG-DEEL',
      'REK-HANDIG-PLUS-MIN',
      'REK-TAFELS-UITBREIDEN',
      'REK-GEMENGD-TOETS',
    ],
  },
  // ── REK-SCHATTEN specific misconceptions ─────────────────────────────
  {
    id: 'rek.schatten-exact-als-fout',
    label: 'Schat: denkt dat exact antwoord fout is',
    module: 'Basisrekenen',
    description:
      'Bij "welk antwoord kan niet kloppen"-vragen kiest leerling het exacte antwoord omdat het niet identiek is aan de schatting.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-klein-verschil-is-fout',
    label: 'Schat: klein verschil zien als fout',
    module: 'Basisrekenen',
    description:
      'Leerling denkt dat een antwoord fout is als het iets afwijkt van de schatting, in plaats van te kijken naar de ordegrootte.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-schatting-is-fout',
    label: 'Schat: denkt dat schatting niet kan kloppen',
    module: 'Basisrekenen',
    description:
      'Leerling kiest de schatting zelf als "kan niet kloppen", omdat het niet het exacte antwoord is.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-afronden-te-ver',
    label: 'Schat: rondt te ver af',
    module: 'Basisrekenen',
    description:
      'Leerling rondt af naar een tiental/honderdtal dat verder weg ligt dan het dichtstbijzijnde, waardoor de schatting onnauwkeurig wordt.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-maar-een-getal',
    label: 'Schat: rondt maar één getal af',
    module: 'Basisrekenen',
    description:
      'Leerling rondt slechts één van twee operanden af bij schatten, waardoor de schatting bijna een exacte berekening wordt.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-afronden-verkeerde-richting',
    label: 'Schat: rondt verkeerde kant op',
    module: 'Basisrekenen',
    description:
      'Leerling rondt naar beneden af terwijl naar boven afronden dichter bij ligt (of omgekeerd) — kiest niet het dichtstbijzijnde getal.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-vergeet-vermenigvuldigen',
    label: 'Schat: vergeet aantal mee te rekenen',
    module: 'Basisrekenen',
    description:
      'Bij context-schattingen geeft leerling alleen de prijs/eenheid en vergeet te vermenigvuldigen met het aantal stuks.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-deeltal-als-antwoord',
    label: 'Schat: geeft deeltal in plaats van quotiënt',
    module: 'Basisrekenen',
    description:
      'Bij een schat-deling geeft leerling het deeltal (bv. 200) als antwoord in plaats van het quotiënt (bv. 50).',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-keer-ipv-delen',
    label: 'Schat: vermenigvuldigt i.p.v. te delen',
    module: 'Basisrekenen',
    description:
      'Bij een verdelingsvraag in een schat-context vermenigvuldigt leerling i.p.v. te delen.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-te-nauwkeurig',
    label: 'Schat: te nauwkeurig afgerond',
    module: 'Basisrekenen',
    description:
      'Leerling rondt af naar getallen die nog steeds moeilijk uit het hoofd te berekenen zijn (bv. 620−190) i.p.v. naar handige ronde getallen (600−200).',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.estimation-error',
    label: 'Tussenschatting bij compenseren wijkt te ver af',
    module: 'Basisrekenen',
    description:
      'Bij een handige-rekenen-strategie (compenseren, splitsen) kiest leerling een tussenschatting die te grof is om mee terug te corrigeren, waardoor de eindwaarde te ver van de exacte uitkomst ligt. Onderscheid met de rek.schatten-* familie: dáár gaat het om de schat-context zelf; hier om compensatie-strategie.',
    typicalFamilies: ['REK-HANDIG-DEEL', 'REK-HANDIG-PLUS-MIN'],
  },
  // ── REK-CIJF-KEER specific misconceptions ────────────────────────────
  {
    id: 'rek.multiplication-error',
    label: 'Cijferende vermenigvuldigingsfout',
    module: 'Basisrekenen',
    description:
      'Leerling maakt een fout in de cijferende vermenigvuldiging (verkeerd tussenproduct, verschoven kolom, verkeerde factor).',
    typicalFamilies: ['REK-CIJF-KEER', 'REK-TAFELS-TOEPASSEN'],
  },
  {
    id: 'rek.multiplication-zero-placeholder',
    label: 'Nul-placeholder vergeten bij staartvermenigvuldiging',
    module: 'Basisrekenen',
    description:
      'Bij cijferende vermenigvuldiging met een tweecijferige vermenigvuldiger vergeet leerling de 0 als plaatshouder op de tweede regel (vermenigvuldiging met tientallen), waardoor het tweede tussenproduct in de verkeerde kolommen staat en de optelling fout uitvalt.',
    typicalFamilies: ['REK-CIJF-KEER'],
  },
  {
    id: 'rek.zero-omission',
    label: 'Nul in quotiënt vergeten',
    module: 'Basisrekenen',
    description:
      'Leerling vergeet de 0 in het quotiënt bij staartdeling wanneer de deler niet in een tussenrest past (bv. 605 ÷ 5 → 11 i.p.v. 121).',
    typicalFamilies: ['REK-CIJF-DEEL'],
  },
  // ── REK-CONTEXT (meerstaps) specific misconceptions ──────────────────
  {
    id: 'rek.context-wrong-order',
    label: 'Verkeerde volgorde van stappen in meerstapsprobleem',
    module: 'Basisrekenen',
    description:
      'Leerling voert de stappen in een meerstapsprobleem in de verkeerde volgorde uit (bv. korting toepassen vóór het totaal te berekenen).',
    typicalFamilies: ['REK-CONTEXT-MEERSTAPS', 'REK-CONTEXT-PLUS-MIN'],
  },
  {
    id: 'rek.context-wrong-scale',
    label: 'Verkeerde schaalfactor uit context',
    module: 'Basisrekenen',
    description:
      'Leerling kiest de verkeerde schaal- of vermenigvuldigingsfactor (bv. 3 i.p.v. 3,5; afgeronde waarde i.p.v. exacte).',
    typicalFamilies: ['REK-CONTEXT-MEERSTAPS', 'REK-CONTEXT-KEER-DEEL'],
  },
  {
    id: 'rek.rounding-overshoot',
    label: 'Te ver naar boven afgerond',
    module: 'Basisrekenen',
    description:
      'Leerling rondt af naar een eenheid groter dan nodig in een context (bv. naar 100-tal terwijl 10-tal volstaat), waardoor de schatting te hoog wordt.',
    typicalFamilies: ['REK-CONTEXT-MEERSTAPS', 'REK-SCHATTEN'],
  },
  // ── REK-SCHATTEN: nieuwe specifieke misconcepties ────────────────────
  {
    id: 'rek.schatten-vergeet-optellen',
    label: 'Schat: vergeet tweede operand op te tellen',
    module: 'Basisrekenen',
    description:
      'Bij meerstaps-schattingen vergeet leerling de tweede dag/portie/operand mee op te tellen.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-nabijheid-boven-deelbaarheid',
    label: 'Schat: kiest dichtstbijzijnd getal boven deelbaar getal',
    module: 'Basisrekenen',
    description:
      'Bij een schat-deling kiest leerling het dichtstbijzijnde ronde getal als afronding, ook als een iets verder weg gelegen getal beter deelbaar is (bv. 500 i.p.v. 480 voor ÷ 6).',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-ronde-getal-altijd-handig',
    label: 'Schat: denkt dat ronde getallen altijd handig zijn',
    module: 'Basisrekenen',
    description:
      'Leerling neemt aan dat afronden op een rond getal altijd de handigste strategie is, ongeacht de operatie (negeert deelbaarheid bij delen).',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-strategie-onverschillig',
    label: 'Schat: ziet geen verschil tussen schattingsstrategieën',
    module: 'Basisrekenen',
    description:
      'Leerling ziet geen kwaliteitsverschil tussen verschillende schattingsstrategieën — kan niet beoordelen welke strategie voor déze opgave optimaal is.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-richting-onbewust',
    label: 'Schat: mixt over- en onderschatting onbewust',
    module: 'Basisrekenen',
    description:
      'In een budget-/voldoende-context mixt leerling over- en onderschatting zonder bewuste richting — schat één kant naar boven, andere naar beneden, zonder check tegen de vraag-richting (is het genoeg?).',
    typicalFamilies: ['REK-SCHATTEN'],
  },
  {
    id: 'rek.schatten-onderschatten-bij-budget',
    label: 'Schat: onderschatten bij "is het genoeg?"',
    module: 'Basisrekenen',
    description:
      'Bij een budget-vraag ("is dit genoeg?") onderschat leerling de kosten — eenzijdig naar beneden afronden bewijst niets over genoegzaamheid; je moet juist naar boven afronden om zeker te zijn.',
    typicalFamilies: ['REK-SCHATTEN'],
  },
]
